import { User, logout } from '../../../lib/auth-store';
import {
  LayoutDashboard,
  Plus,
  Layers,
  Play,
  TrendingUp,
  LogOut,
  ChevronRight,
  Sparkles,
  Shield,
  Clock,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Zap,
  Building2,
  ShieldCheck,
  FileText,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandMark } from '@rynora/theme';
import { getFlowBuilderUrl } from '../../../lib/config';

interface DashboardPageProps {
  user: User;
  onLogout: () => void;
  onNavigate: (page: string) => void;
}

type FlowBuilderIntent = 'new-flow' | undefined;

function openFlowBuilder(intent?: FlowBuilderIntent) {
  const token = localStorage.getItem('rynora_token');
  const base = getFlowBuilderUrl();
  // Caddy mounts the flow-builder behind `handle /app/*` (see
  // /etc/caddy/Caddyfile). The matcher requires the trailing slash —
  // a bare `/app` falls through to the landing-page `handle {}` block
  // and silently lands the user back on the marketing site. We
  // canonicalise to `<base>/` here so the new tab arrives at the SPA
  // directly without bouncing through the 308 redirect Caddy adds for
  // the same reason. Both forms are correct; this just skips the
  // round-trip and keeps the URL bar clean of an intermediate /app.
  const baseWithSlash = base.endsWith('/') ? base : `${base}/`;
  // The hash-fragment is also our intent channel: callers labelled
  // "Create Automation Flow" pass `intent='new-flow'` so the SPA's
  // `initTokenFromUrl` preserves it past the token consumption step
  // and the App-shell hash-router routes the user straight into the
  // editor with a fresh blank flow stubbed in their most-recently
  // used project (or a scaffolded one for fresh accounts). Callers
  // labelled "Test Flows" (project list / sidebar) pass nothing and
  // get the historical project-list landing.
  const params = new URLSearchParams();
  if (token) params.set('token', token);
  if (intent) params.set('intent', intent);
  const qs = params.toString();
  const url = qs ? `${baseWithSlash}#${qs}` : baseWithSlash;
  window.open(url, '_blank');
}

export function DashboardPage({ user, onLogout }: DashboardPageProps) {
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    onLogout();
  };

  const stats = [
    { label: 'Total Flows', value: '0', icon: Layers, color: 'var(--accent)' },
    { label: 'Runs Today', value: '0', icon: Play, color: 'var(--success)' },
    { label: 'Pass Rate', value: '--', icon: CheckCircle, color: '#22c55e' },
    { label: 'Avg Duration', value: '--', icon: Clock, color: '#f59e0b' },
  ];

  return (
    <div className="dashboard">
      {/* Sidebar */}
      <aside className="dash-sidebar">
        <div className="dash-sidebar-header">
          <Link to="/" className="dash-logo" aria-label="Rynora — home">
            <BrandMark variant="compact" />
          </Link>
        </div>

        <nav className="dash-nav">
          <div className="dash-nav-section">
            <div className="dash-nav-title">Main</div>
            <a className="dash-nav-item active">
              <LayoutDashboard size={16} /> Dashboard
            </a>
            <a className="dash-nav-item" onClick={() => openFlowBuilder()}>
              <Layers size={16} /> Test Flows
              <ChevronRight size={14} style={{ marginLeft: 'auto' }} />
            </a>
            <a className="dash-nav-item dash-nav-item--disabled" aria-disabled="true">
              <Play size={16} /> Runs
              <span className="dash-badge">Soon</span>
            </a>
            <a className="dash-nav-item dash-nav-item--disabled" aria-disabled="true">
              <BarChart3 size={16} /> Reports
              <span className="dash-badge">Soon</span>
            </a>
          </div>

          <div className="dash-nav-section">
            <div className="dash-nav-title">Tools</div>
            <a className="dash-nav-item dash-nav-item--disabled" aria-disabled="true">
              <Sparkles size={16} /> AI Recorder
              <span className="dash-badge">Soon</span>
            </a>
            <a className="dash-nav-item dash-nav-item--disabled" aria-disabled="true">
              <Shield size={16} /> Self-Healing
              <span className="dash-badge">Soon</span>
            </a>
          </div>

          <div className="dash-nav-section">
            <div className="dash-nav-title">Organization</div>
            <a className="dash-nav-item" onClick={() => navigate('/dashboard/sso')}>
              <Building2 size={16} /> Single Sign-On
            </a>
            <a className="dash-nav-item" onClick={() => navigate('/dashboard/security')}>
              <ShieldCheck size={16} /> Security
            </a>
            <a className="dash-nav-item" onClick={() => navigate('/dashboard/dsar')}>
              <FileText size={16} /> Data requests
            </a>
          </div>
        </nav>

        <div className="dash-sidebar-footer">
          <div className="dash-plan">
            <div className="dash-plan-label">Current Plan</div>
            <div className="dash-plan-name">{user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}</div>
            <button
              className="dash-upgrade-btn"
              onClick={() => {
                window.location.assign('/#pricing');
              }}
              title="See pricing tiers"
            >
              Upgrade
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="dash-main">
        {/* Top bar */}
        <header className="dash-topbar">
          <div>
            <h1 className="dash-page-title">Dashboard</h1>
            <p className="dash-page-subtitle">Welcome back, {user.name.split(' ')[0]}</p>
          </div>
          <div className="dash-topbar-right">
            {/* Settings page isn't wired yet — hide the entry point until
                it lands instead of shipping a dead button that does
                nothing on click (testers WILL click it). The lucide
                Settings import is kept for the eventual wire-up. */}
            <div className="dash-user-menu">
              <div className="dash-avatar">{user.avatar}</div>
              <div className="dash-user-info">
                <div className="dash-user-name">{user.name}</div>
                <div className="dash-user-email">{user.email}</div>
              </div>
              <button className="dash-icon-btn" onClick={handleLogout} title="Sign out">
                <LogOut size={16} />
              </button>
            </div>
          </div>
        </header>

        {/* Stats */}
        <div className="dash-stats">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div key={i} className="dash-stat-card">
                <div className="dash-stat-icon" style={{ color: stat.color, background: stat.color + '18' }}>
                  <Icon size={18} />
                </div>
                <div>
                  <div className="dash-stat-value">{stat.value}</div>
                  <div className="dash-stat-label">{stat.label}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick actions */}
        <div className="dash-section">
          <h2 className="dash-section-title">Quick Actions</h2>
          <div className="dash-actions-grid">
            <button className="dash-action-card" onClick={() => openFlowBuilder('new-flow')}>
              <div className="dash-action-icon" style={{ background: 'var(--accent-glow)', color: 'var(--accent)' }}>
                <Plus size={22} />
              </div>
              <h3>Create Automation Flow</h3>
              <p>Build a new automation flow visually with drag-and-drop</p>
            </button>

            <button className="dash-action-card dash-action-card--disabled" disabled aria-disabled="true">
              <div className="dash-action-icon" style={{ background: 'rgba(34, 197, 94, 0.12)', color: '#22c55e' }}>
                <Sparkles size={22} />
              </div>
              <h3>
                Generate from NL <span className="dash-badge">Soon</span>
              </h3>
              <p>Describe a test in plain English and let AI build it</p>
            </button>

            <button className="dash-action-card dash-action-card--disabled" disabled aria-disabled="true">
              <div className="dash-action-icon" style={{ background: 'rgba(245, 158, 11, 0.12)', color: '#f59e0b' }}>
                <Zap size={22} />
              </div>
              <h3>
                Crawl My App <span className="dash-badge">Soon</span>
              </h3>
              <p>Point Rynora at your URL to auto-discover test flows</p>
            </button>

            <button className="dash-action-card dash-action-card--disabled" disabled aria-disabled="true">
              <div className="dash-action-icon" style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7' }}>
                <TrendingUp size={22} />
              </div>
              <h3>
                View Reports <span className="dash-badge">Soon</span>
              </h3>
              <p>See test results, trends, and flakiness analysis</p>
            </button>
          </div>
        </div>

        {/* Getting started */}
        <div className="dash-section">
          <h2 className="dash-section-title">Getting Started</h2>
          <div className="dash-onboarding">
            <div className="dash-onboard-step completed">
              <div className="dash-onboard-check">
                <CheckCircle size={18} />
              </div>
              <div>
                <h4>Create your account</h4>
                <p>You're all set up and ready to go</p>
              </div>
            </div>
            <div className="dash-onboard-step">
              <div className="dash-onboard-num">2</div>
              <div>
                <h4>Create your first automation flow</h4>
                <p>Use the visual builder or natural language to create an automation</p>
              </div>
              <button className="dash-onboard-btn" onClick={() => openFlowBuilder('new-flow')}>
                Start <ChevronRight size={14} />
              </button>
            </div>
            <div className="dash-onboard-step">
              <div className="dash-onboard-num">3</div>
              <div>
                <h4>Run your first test</h4>
                <p>Execute your flow and see the self-healing engine in action</p>
              </div>
            </div>
            <div className="dash-onboard-step">
              <div className="dash-onboard-num">4</div>
              <div>
                <h4>Connect your CI/CD</h4>
                <p>Integrate Rynora into your deployment pipeline</p>
              </div>
            </div>
          </div>
        </div>

        {/* Recent activity (empty state) */}
        <div className="dash-section">
          <h2 className="dash-section-title">Recent Activity</h2>
          <div className="dash-empty">
            <AlertTriangle size={20} />
            <p>No test runs yet. Create a flow and run it to see results here.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
